import "./style.css";
import { useDispatch, useSelector } from "react-redux";
import { openModal } from "../../../Redux/Action/Action";
import { useEffect, useRef, useState } from "react";
import { apiData } from "../../../actions";
import { ArrowLeftOutlined } from "@ant-design/icons";
import ReactMarkdown from "react-markdown";
import pie from "../../../assets/images/pie-chart.png"
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Pagination } from "swiper/modules";
import defaultFav from '../../../assets/images/result-overview-figure.jpg'
import "swiper/css";
import "swiper/css/pagination";
import SpendDonutChart from "./SpendDonutChart";


export function TypingText({
    text = "",
    speed = 20,
    onComplete,
    className = ""
}) {
    const [displayed, setDisplayed] = useState("");
    const indexRef = useRef(0);
    const intervalRef = useRef(null);

    useEffect(() => {
        if (!text) {
            setDisplayed("");
            return;
        }

        // 🔁 reset on text change
        setDisplayed("");
        indexRef.current = 0;

        if (intervalRef.current) {
            clearInterval(intervalRef.current);
        }

        intervalRef.current = setInterval(() => {
            setDisplayed(() => {
                if (indexRef.current < text.length) {
                    const next = text.slice(0, indexRef.current + 1);
                    indexRef.current += 1;
                    return next;
                } else {
                    clearInterval(intervalRef.current);
                    intervalRef.current = null;
                    onComplete?.();
                    return text;
                }
            });
        }, speed);

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
        };
    }, [text, speed, onComplete]);

    return (
        <div className={className}>
            <ReactMarkdown>{displayed}</ReactMarkdown>
        </div>
    );
}


export default function TravelPreferencesResult({ formData, onBack, tripData }) {
    const dispatch = useDispatch();
    const place = useSelector((state) => state.place);

    const [hotelDealsData, setHotelsDealsData] = useState([]);
    const [flightDealsData, setFlightDealsData] = useState([]);
    const [loader, setLoader] = useState(false);

    const [stage, setStage] = useState(null);
    const [finishedStages, setFinishedStages] = useState([]);
    const [activeIndex, setActiveIndex] = useState(0);
    const [cityImages, setCityImages] = useState([]);

    const hasAutoOpenedRef = useRef(false);


    // const bottomRef = useRef(null);


    const getAccordionColor = (index) => {
        const colors = [
            "#2D8193", // light blue
            "#F68266", // light green
            "#AF82EE", // light orange
            "#FFA953", // light pink
            "#71A7F8", // light purple
        ];
        return colors[index % colors.length];
    };


    const destinationKey =
        (formData?.destination || place || "default").toLowerCase();
    const shuffledImagesRef = useRef({});

    const fetchCityImages = async (city) => {
        try {
            // ✅ reuse already shuffled images for same city
            if (shuffledImagesRef.current[city]) {
                setCityImages(shuffledImagesRef.current[city]);
                return;
            }

            const payload = { city };
            const res = await apiData.Ban_Image(payload);
            const hits = res?.data?.hits || [];

            let finalImages = [];

            if (hits.length > 0) {
                const images = hits
                    .map((img) => img.largeImageURL)
                    .filter(Boolean);

                // ✅ pick RANDOM 3 images ONLY
                finalImages = images
                    .sort(() => 0.5 - Math.random())
                    .slice(0, 3);
            } else {
                // ✅ fallback must be ARRAY
                finalImages = [defaultFav, defaultFav, defaultFav];
            }

            shuffledImagesRef.current[city] = finalImages;
            setCityImages(finalImages);

        } catch (error) {
            console.error("Image fetch error:", error);
            setCityImages([defaultFav, defaultFav, defaultFav]);
        }
    };





    useEffect(() => {
        fetchCityImages(destinationKey);
    }, [destinationKey]);

    /* ✅ SAFE STAGE INIT */
    useEffect(() => {
        if (Array.isArray(tripData?.days) && tripData.days.length > 0) {
            setStage("day-0");
        }
    }, [tripData]);

    useEffect(() => {
        if (
            tripData?.days?.length > 0 &&
            !hasAutoOpenedRef.current
        ) {
            setActiveIndex(0); // open first day ONLY ONCE
            hasAutoOpenedRef.current = true;
        }
    }, [tripData?.days?.length]);



    const handleComplete = (nextStage) => {
        setFinishedStages((prev) =>
            prev.includes(stage) ? prev : [...prev, stage]
        );
        if (nextStage) setStage(nextStage);
    };

    const isFinished = (stageName) => finishedStages.includes(stageName);

    // useEffect(() => {
    //     if (!tripData?.days?.length) return;

    //     bottomRef.current?.scrollIntoView({
    //         behavior: "smooth",
    //         block: "end",
    //     });
    // }, [tripData?.days?.length]);


    useEffect(() => {
        window.scrollTo({ top: 0, behavior: "smooth" });
    }, []);

    const GetFlightsDeals = async () => {
        setLoader(true)
        let payload = {
            city: formData?.destination || place || "chicago",
            keyword: "Flights",
            limit: 0
        };
        let apiRes = await apiData.getRandomDeals(payload);
        if (apiRes?.status >= 200 && apiRes?.status <= 399) {
            setFlightDealsData(
                apiRes?.data?.data
            );
            setLoader(false);
        }
        if (apiRes?.status >= 400 && apiRes?.status <= 500) {
            console.log("error");
        }
    };

    const GetHotelsDeals = async () => {
        setLoader(true);
        let payload = {
            city: formData?.destination || place || "chicago",
            keyword: "Hotels",
            limit: 0 // try 0 or remove if API supports unlimited
        };

        let apiRes = await apiData.getRandomDeals(payload);

        if (apiRes?.status >= 200 && apiRes?.status <= 399) {
            const scriptTag = apiRes?.data?.data?.[0]?.content;

            if (scriptTag && typeof scriptTag === "string") {
                // Extract base script src
                const match = scriptTag.match(/src="([^"]+)"/);

                if (match) {
                    const scriptSrc = match[1];

                    // instead of hardcoding 10 or 50, extract current limit
                    const limitMatch = scriptSrc.match(/limit=(\d+)/);
                    const actualLimit = limitMatch ? parseInt(limitMatch[1]) : 10;

                    // Create two versions dynamically
                    const scriptSrcShort = scriptSrc.replace(
                        /limit=\d+/,
                        `limit=${Math.min(actualLimit, 10)}`
                    );
                    const scriptSrcAll = scriptSrc; // use full from API

                    setHotelsDealsData([scriptSrcShort, scriptSrcAll]);
                }
            } else {
                console.warn("No scriptTag found in API response");
            }
        } else {
            console.error("Error fetching hotel deals");
        }
        setLoader(false);
    };



    const handleOpenModal = (title, content) => {
        if (typeof content === "string") {
            // hotels → script
            dispatch(openModal({ title, scriptSrc: content, loader }));
        } else if (Array.isArray(content)) {
            // flights → array of objects
            dispatch(openModal({ title, images: content, loader }));
        } else {
            console.warn("Unsupported modal content type:", content);
        }
    };



    useEffect(() => {
        if (hotelDealsData.length > 0) {
            // Clear old widget before appending again
            const container = document.getElementById("deals-widget");
            container.innerHTML = "";

            const script = document.createElement("script");
            script.src = hotelDealsData[0]; // ✅ show only 10 deals
            script.async = true;
            script.charset = "UTF-8";
            container.appendChild(script);
        }
    }, [hotelDealsData]);


    useEffect(() => {
        GetFlightsDeals()
        GetHotelsDeals()
    }, [])

    // useEffect(() => {
    //     if (!tripData?.days?.length) return;

    //     bottomRef.current?.scrollIntoView({ behavior: "smooth" });
    // }, [tripData?.days?.length]);

    const handleAccordionClick = (index) => {
        setActiveIndex((prev) => (prev === index ? null : index));
    };



    return (

        <section className="section-equal-padding">
            <div className="container-fluid">

                <div className="accordion result-overview-block" id="accordionExample">
                    {/* ===============================
                       TRIP OVERVIEW
                    =============================== */}
                    <div className="accordion-item">
                        <div className="accordion-header">
                            <button
                                className="accordion-button"
                                type="button"
                                data-bs-toggle="collapse"
                                data-bs-target="#collapseOne"
                            >
                                Trip Overview
                            </button>
                        </div>

                        <div
                            id="collapseOne"
                            className="accordion-collapse collapse show"
                        >
                            <div className="pt-3">
                                <div className="row gx-3">
                                    {(formData?.destination || formData?.departure) && (
                                        <div className="col-lg-4 mb-3">
                                            <div className="overview-list bg-white rounded-4 p-4">
                                                <ul>
                                                    {formData?.destination && (
                                                        <li>
                                                            Departure city:{" "}
                                                            <strong>{formData?.destination}</strong>
                                                        </li>
                                                    )}
                                                    {formData?.departure && (
                                                        <li>
                                                            Destination:{" "}
                                                            <strong>{formData?.departure}</strong>
                                                        </li>
                                                    )}
                                                </ul>
                                            </div>
                                        </div>
                                    )}

                                    {(formData?.travelDate ||
                                        formData?.budget ||
                                        formData?.days) && (
                                            <div className="col-lg-4 mb-3">
                                                <div className="overview-list bg-white rounded-4 p-4">
                                                    <ul>
                                                        {formData?.travelDate && (
                                                            <li>
                                                                Date:{" "}
                                                                <strong>
                                                                    {new Date(
                                                                        formData?.travelDate
                                                                    ).toLocaleDateString("en-US", {
                                                                        day: "numeric",
                                                                        month: "long",
                                                                        year: "numeric",
                                                                    })}
                                                                </strong>
                                                            </li>
                                                        )}
                                                        {formData?.days && (
                                                            <li>
                                                                Travelling Days:{" "}
                                                                <strong>
                                                                    {formData?.days}{" "}
                                                                    {formData?.days === 1 ? "day" : "days"}
                                                                </strong>
                                                            </li>
                                                        )}
                                                        {formData?.budget && (
                                                            <li>
                                                                Budget:{" "}
                                                                <strong>$ {formData?.budget}</strong>
                                                            </li>
                                                        )}
                                                    </ul>
                                                </div>
                                            </div>
                                        )}

                                    {((Array.isArray(formData?.activities) &&
                                        formData?.activities.length > 0) || (formData?.travelWith || formData?.members)) && (
                                            <div className="col-lg-4 mb-3">
                                                <div className="overview-list bg-white rounded-4 p-4">
                                                    <ul>
                                                        <li>
                                                            Travel Style:{" "}
                                                            <strong>
                                                                {formData?.activities.length > 0
                                                                    ? formData?.activities.join(" , ")
                                                                    : "-"}
                                                            </strong>
                                                        </li>
                                                        <li>
                                                            Ideal For:{" "}
                                                            <strong>
                                                                {formData?.travelWith || formData?.members}
                                                            </strong>
                                                        </li>
                                                    </ul>
                                                </div>
                                            </div>
                                        )}
                                </div>
                            </div>

                            <div className="row">
                                <div className="col-12 col-sm-12 col-md-3 col-lg-6 mb-3 mb-md-0">
                                    <div className="trip-figure-view rounded-4 overflow-hidden">
                                        <Swiper
                                            key={cityImages.join("-")}   // 🔥 THIS IS THE FIX
                                            modules={[Autoplay, Pagination]}
                                            spaceBetween={10}
                                            slidesPerView={1}
                                            loop={true}
                                            autoplay={{
                                                delay: 2500,
                                                disableOnInteraction: false,
                                            }}
                                            pagination={{ clickable: true }}
                                            className="destination-swiper"
                                        >
                                            {cityImages.map((img, index) => (
                                                <SwiperSlide key={index}>
                                                    <img
                                                        src={img}
                                                        alt={`${destinationKey}-${index}`}
                                                        className="w-100"
                                                        style={{
                                                            height: "430px",
                                                            objectFit: "cover",
                                                            borderRadius: "16px",
                                                        }}
                                                    />
                                                </SwiperSlide>
                                            ))}
                                        </Swiper>

                                    </div>
                                </div>

                                <div className="col-12 col-sm-12 col-md-6 col-lg-6">
                                    <div className="bg-white rounded-4 p-4 h-100">
                                        {!tripData?.days || tripData.days.length === 0 ? (
                                            <div className="text-center py-5">
                                                <div className="spinner-border text-primary mb-3" />
                                                <div className="fw-semibold">Analyzing your trip data…</div>
                                            </div>
                                        ) : (
                                            <SpendDonutChart days={tripData.days} />
                                        )}

                                    </div>
                                </div>

                            </div>


                        </div>
                    </div>

                    {/* ===============================
                       DAILY ITINERARY
                    =============================== */}
                    {Array.isArray(tripData?.days) &&
                        tripData?.days.map((dayObj, i) => {
                            const accordionId = `collapse-day-${i}`;
                            const isOpen = activeIndex === i;
                            return (

                                <div key={i} className="accordion-item teal-background">
                                    <div className="accordion-header">
                                        <button
                                            className={`accordion-button ${!isOpen ? "collapsed" : ""}`}
                                            type="button"
                                            onClick={() => handleAccordionClick(i)}
                                            aria-expanded={isOpen}
                                            style={{ backgroundColor: getAccordionColor(i) }}
                                        >
                                            {dayObj?.dayTitle || `Day ${i + 1}`}
                                        </button>
                                    </div>

                                    <div
                                        id={accordionId}
                                        className={`accordion-collapse collapse ${isOpen ? "show" : ""}`}
                                    >
                                        <div className="pt-3">
                                            <div className="row gx-3">

                                                {dayObj?.highlights && (<div className="col-12 col-sm-12 col-md-6 col-lg-4 mb-3">
                                                    <div className="intro-overview-list bg-white rounded-4 p-4">
                                                        <h4>Highlights</h4>
                                                        <ul>
                                                            {dayObj?.highlights?.map((item, idx) => (
                                                                <li key={idx}>{item}</li>
                                                            ))}
                                                        </ul>
                                                    </div>
                                                </div>)}



                                                {dayObj?.mustSee && (<div className="col-12 col-sm-12 col-md-6 col-lg-4 mb-3">
                                                    <div className="intro-overview-list bg-white rounded-4 p-4">
                                                        <h4>Must-See</h4>
                                                        <ul>
                                                            {dayObj?.mustSee?.map((item, idx) => (
                                                                <li key={idx}>{item}</li>
                                                            ))}
                                                        </ul>
                                                    </div>
                                                </div>)}



                                                {dayObj?.foodPicks && (<div className="col-12 col-sm-12 col-md-6 col-lg-4 mb-3">
                                                    <div className="intro-overview-list bg-white rounded-4 p-4">
                                                        <h4>Food Picks</h4>
                                                        <ul>
                                                            {dayObj?.foodPicks?.map((item, idx) => (
                                                                <li key={idx}>{item}</li>
                                                            ))}
                                                        </ul>
                                                    </div>
                                                </div>)}


                                                {dayObj?.tip && (
                                                    <div className="col-12 col-sm-12 col-md-6 col-lg-6 mb-3 mb-md-0">
                                                        <div className="bg-white rounded-4 p-4 h-100">
                                                            <div className="tip-block"><strong>Tip:</strong> {dayObj?.tip}</div>
                                                        </div>
                                                    </div>

                                                )}

                                                {dayObj?.estimatedSpend && (
                                                    <div className="col-12 col-sm-12 col-md-6 col-lg-6">
                                                        <div className="bg-white rounded-4 px-4 py-3 h-100">
                                                            <div className="est-spend">Estimated Spend (Day {i + 1})</div>
                                                            <div className="est-price">{dayObj?.estimatedSpend}</div>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )

                            // <div key={i} ref={bottomRef} className="accordion-item teal-background">
                        })}

                </div>

                <div className="deals-container">
                    <div className="deal-section">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h4 className="deal-title">Flight Specials</h4>
                            <button
                                className="view-more btn btn-link p-0"
                                onClick={() => handleOpenModal("Flight Specials", flightDealsData)}
                            >
                                View More →
                            </button>
                        </div>
                        {loader ? (
                            <div className="text-center p-5">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Loading flights...</span>
                                </div>
                            </div>
                        ) : (
                            <div className="row">
                                {flightDealsData?.slice(0, 8)?.map((ele) => {
                                    return (
                                        <div className="col-lg-3 col-12">
                                            <a href={ele?.link} target="_blank" rel="noreferrer">
                                                <div data-aos="zoom-in-right" className="discount-blocks blog">
                                                    <img loading="lazy" src={`${ele?.image}`} alt='Flight-deals' />
                                                    <h5>{ele?.city_name} <i className="fa fa-plane flight_deal_icon" aria-hidden="true"></i> {formData.destination || place || "chicago"}</h5>
                                                </div> </a>
                                        </div>
                                    )
                                })}
                            </div>
                        )}
                    </div>

                    <div className="deal-section mt-4">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h4 className="deal-title">Best Hotel Deals</h4>
                            <button
                                className="view-more btn btn-link p-0"
                                onClick={() => handleOpenModal("Hotel Deals", hotelDealsData[1])}
                            >
                                View More →
                            </button>
                        </div>
                        {loader ? (
                            <div className="text-center p-5">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Loading hotels...</span>
                                </div>
                            </div>
                        ) : (
                            <div className="row">

                                <div className="col-md-4 mb-3 w-100">
                                    <div className="deal-card card h-100">
                                        <div className="card-body text-center">
                                            <div id="deals-widget"></div>
                                        </div>
                                    </div>
                                </div>

                            </div>
                        )}
                    </div>

                    <div className="text-center mt-4 d-inline-block w-100">
                        <button className="form-button fill-button mx-0" type="submit" onClick={onBack} > <ArrowLeftOutlined
                            style={{ fontSize: "20px", cursor: "pointer", marginRight: "10px" }}
                        // 👈 reset formData & go back
                        /> Back</button>
                    </div>

                </div>

            </div>
        </section>

    );
}